import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Permission } from "@alm/policy";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { useSwitchToPlatform } from "@/components/app/workspace/switch-to-platform";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { RoleEditor } from "./-components/role-editor";
import { RoleHolders } from "./-components/role-holders";

export const Route = createFileRoute("/_authenticated/_administration/settings/roles/$roleId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.administration.roles.get.queryOptions({ input: { roleId: params.roleId } }),
    ),
  staticData: { fullWidth: true },
  component: EditRolePage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** One role, on a page of its own. A native role opens read-only with Copy as its one action, for
 * everybody: People AI changes native roles from the platform console, not here. */
function EditRolePage() {
  const { roleId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const platform = useSwitchToPlatform();
  const canEdit = useCanOrganizationWide("organization:manage-roles");

  const { data: role } = useSuspenseQuery(
    orpc.administration.roles.get.queryOptions({ input: { roleId } }),
  );
  const grantable = useQuery(orpc.administration.roles.permissions.queryOptions({}));

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.roles.key() });
  const update = useMutation(
    orpc.administration.roles.update.mutationOptions({
      onSuccess: async (saved) => {
        toast.success(
          role.assignedCount > 0
            ? `${saved.name} saved. ${role.assignedCount} ${role.assignedCount === 1 ? "person has" : "people have"} the new permissions now.`
            : `${saved.name} saved`,
        );
        await refresh();
      },
      onError: fail,
    }),
  );
  const duplicate = useMutation(
    orpc.administration.roles.duplicate.mutationOptions({
      onSuccess: async (copy) => {
        toast.success(`Copied as ${copy.name}`);
        await refresh();
        await navigate({ to: "/settings/roles/$roleId", params: { roleId: copy.id } });
      },
      onError: fail,
    }),
  );

  if (grantable.isPending) return <Skeleton className="h-96 w-full" />;
  if (grantable.isError) {
    return (
      <ReadFailed
        message="The permissions could not be loaded."
        error={grantable.error}
        retrying={grantable.isFetching}
        onRetry={() => void grantable.refetch()}
      />
    );
  }

  return (
    <RoleEditor
      key={`${role.id}-${String(role.updatedAt)}`}
      title={role.name}
      initial={{
        name: role.name,
        description: role.description ?? "",
        permissions: role.permissions as Permission[],
      }}
      grantable={grantable.data as Permission[]}
      readOnly={role.isSystemRole || !canEdit}
      pending={update.isPending}
      headerBadge={
        role.isSystemRole ? (
          <Badge variant="outline" className="font-normal">
            Native, read only
          </Badge>
        ) : null
      }
      aside={
        <>
          {role.isSystemRole ? (
            <Card>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground text-pretty">
                  A native role: every organization has it and the platform keeps it current, so it
                  cannot be changed here. Give it as it is, or copy it and change the copy.
                </p>
                {canEdit ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={duplicate.isPending}
                    onClick={() => duplicate.mutate({ roleId: role.id })}
                  >
                    Copy this role
                  </Button>
                ) : null}
                {platform.available ? (
                  <Button
                    className="w-full"
                    variant="ghost"
                    disabled={platform.pending}
                    onClick={platform.openNativeRoles}
                  >
                    Edit it in the platform console
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
          <RoleHolders holders={role.holders} total={role.assignedCount} />
        </>
      }
      onSave={(values) => update.mutate({ roleId: role.id, ...values })}
    />
  );
}
